import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Form,
  Input,
  Switch,
  Progress,
  Spin,
  Tag,
  Empty,
  message,
  Popconfirm,
  Space
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
//   ServerOutlined,
  DesktopOutlined,
  ArrowLeftOutlined,
  SyncOutlined,
  StopOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,DatabaseTwoTone
} from '@ant-design/icons';
import TopBar from '@/Components/TopBar';
import {
  opcuaConnect,
  opcuaBrowseTree,
  opcuaDisconnect,
  opcGetConnections,
  opcCreateConnection,
  opcUpdateConnection,
  opcDeleteConnection,
} from '@/services/opcService';
import type { OpcConnection } from '@/services/opcService';
import './index.less';
import Navbar from "@/Components/navbar";

// ============================================================
// 假接口定义区域 —— 后续需要替换为真实 API 调用
// ============================================================

/**
 * 连接 OPC 服务器并浏览节点树
 * 
 * 流程：
 *   1. POST /api/opcua/connect → 建立连接
 *   2. POST /api/opcua/browse-tree → 递归获取全部点位
 */
const connectAndBrowseNodes = async (
  serverUrl: string,
  connectionId: string,
  _username: string,
  _password: string,
): Promise<{ success: boolean; message: string; totalNodes: number }> => {
  // 1. 连接 OPC UA 服务器
  const connectRes = await opcuaConnect({ url: serverUrl });
  if (!connectRes.success) {
    return { success: false, message: connectRes.message, totalNodes: 0 };
  }

  // 2. 递归浏览节点树，获取全部点位
  const browseData = await opcuaBrowseTree({ nodeId: 'Objects', maxDepth: 3 });
  const points = JSON.parse(browseData.value || '[]');

  // 3. 缓存点位到 localStorage（后续可替换为后端数据库存储）
  const pointData = points.map((p: any) => ({
    id: p.id,
    name: p.displayName || p.name,
    node_id: p.id,
    value: '---',
    unit: '',
    status: 'Good' as const,
    updated_at: new Date().toISOString(),
    group_id: null,
    major_category: '',
    sub_category: '',
    connection_id: connectionId,
  }));

  // 使用连接 UUID 作为缓存键
  const cacheKey = `opc_points_${connectionId}`;
  localStorage.setItem(cacheKey, JSON.stringify(pointData));

  return {
    success: true,
    message: '连接成功',
    totalNodes: browseData.totalCount || pointData.length,
  };
};

/**
 * 【假接口】获取已连接的点位数据列表
 * TODO: 替换为真实 API: GET /api/opc/points?connection_id=xxx
 */
const mockGetPoints = async (_connectionId: string): Promise<any[]> => {
  await new Promise((r) => setTimeout(r, 300));
  // 返回空数组表示暂无数据
  return [];
};

// ============================================================
// 类型定义
// ============================================================

type ViewMode = 'list' | 'form' | 'connecting';

// ============================================================
// DataAccess 组件
// ============================================================

const DataAccess: React.FC<any> = (props) => {
  const { mainContent, extra } = props;
  const navigate = useNavigate();
  // --- 视图状态 ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // --- 连接列表 ---
  const [connections, setConnections] = useState<OpcConnection[]>([]);
  const [loading, setLoading] = useState(false);

  // --- 表单状态 ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  // --- 连接中状态 ---
  const [connectingInfo, setConnectingInfo] = useState<{
    connection: OpcConnection;
    totalNodes: number;
    connectedNodes: number;
    status: 'connecting' | 'scanning' | 'saving' | 'success' | 'error';
    message: string;
  } | null>(null);
  const [connectProgress, setConnectProgress] = useState(0);
  const connectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // --- 加载连接列表 ---
  const loadConnections = useCallback(async () => {
    setLoading(true);
    try {
      const list = await opcGetConnections();
      setConnections(list);
    } catch {
      message.error('加载连接列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // --- 新建连接 ---
  const handleNew = useCallback(() => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      username: 'admin',
      server_url: 'opc.tcp://192.168.2.102:4840',
      auto_connect: true,
      auto_reconnect: true,
    });
    setViewMode('form');
  }, [form]);

  // --- 编辑连接 ---
  const handleEdit = useCallback(
    (conn: OpcConnection) => {
      setEditingId(conn.id);
      form.setFieldsValue({
        name: conn.name,
        server_url: conn.server_url,
        username: conn.username,
        password: conn.password,
        auto_connect: conn.auto_connect === 1,
        auto_reconnect: conn.auto_reconnect === 1,
      });
      setViewMode('form');
    },
    [form],
  );

  // --- 返回列表 ---
  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setEditingId(null);
    form.resetFields();
    // 刷新列表
    loadConnections();
  }, [form, loadConnections]);

  // --- 删除连接 ---
  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await opcDeleteConnection(id);
        message.success('连接已删除');
        await loadConnections();
      } catch {
        message.error('删除失败');
      }
    },
    [loadConnections],
  );

  // --- 保存连接 ---
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const data = {
        name: values.name.trim(),
        server_url: values.server_url.trim(),
        username: values.username.trim(),
        password: values.password || '',
        auto_connect: values.auto_connect ? 1 : 0,
        auto_reconnect: values.auto_reconnect ? 1 : 0,
      };

      if (editingId) {
        await opcUpdateConnection(editingId, data);
        message.success('连接已更新');
      } else {
        await opcCreateConnection(data);
        message.success('连接已创建');
      }
      setViewMode('list');
      loadConnections();
    } catch {
      // 表单验证失败
    }
  }, [form, editingId, loadConnections]);

  // --- 模拟数据快捷填入 ---
  const handleMockFill = useCallback(() => {
    form.setFieldsValue({
      server_url: 'mock://demo',
      name: form.getFieldValue('name') || '模拟演示服务器',
    });
  }, [form]);

  // --- 连接 OPC 服务器 ---
  const handleConnect = useCallback(
    async (connection: OpcConnection) => {
      setViewMode('connecting');
      setConnectProgress(0);
      setConnectingInfo({
        connection,
        totalNodes: 0,
        connectedNodes: 0,
        status: 'connecting',
        message: '正在连接 OPC 服务器...',
      });

      // 启动进度动画（假进度）
      connectTimerRef.current = setInterval(() => {
        setConnectProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 300);

      try {
        const result = await connectAndBrowseNodes(
          connection.server_url,
          connection.id,
          connection.username,
          connection.password,
        );

        if (connectTimerRef.current) clearInterval(connectTimerRef.current);

        if (result.success) {
          setConnectProgress(100);
          setConnectingInfo((prev) =>
            prev
              ? {
                  ...prev,
                  totalNodes: result.totalNodes,
                  connectedNodes: result.totalNodes,
                  status: 'success',
                  message: `连接成功，发现 ${result.totalNodes} 个点位`,
                }
              : null,
          );
          message.success(`连接 OPC 服务器成功，发现 ${result.totalNodes} 个节点`);
           // 2秒后自动返回列表（实际项目会跳转到点位列表页）
          // 连接成功后跳转到点位列表页
          setTimeout(() => {
            // handleBackToList();
            navigate(`/project/home/data/point?connection_id=${encodeURIComponent(connection.id)}`);
          }, 2000);
        } else {
          setConnectingInfo((prev) =>
            prev ? { ...prev, status: 'error', message: result.message } : null,
          );
          message.error(result.message);
        }
      } catch {
        if (connectTimerRef.current) clearInterval(connectTimerRef.current);
        setConnectingInfo((prev) =>
          prev ? { ...prev, status: 'error', message: '连接异常，请检查网络' } : null,
        );
        message.error('连接异常');
      }
    },
    //  [handleBackToList],
    [navigate],
  );

  // --- 终止连接 ---
  const handleCancelConnect = useCallback(() => {
    if (connectTimerRef.current) {
      clearInterval(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    // 调用断开连接接口
    opcuaDisconnect().catch(() => {});
    setConnectingInfo(null);
    setConnectProgress(0);
    setViewMode('list');
    message.info('连接已终止');
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (connectTimerRef.current) clearInterval(connectTimerRef.current);
    };
  }, []);

  // ============================================================
  // 渲染: 连接列表视图
  // ============================================================
  const renderListView = () => (
    <div className="da-list-container">
      <div className="da-list-header">
        <div className="da-icon-box">
          <ThunderboltOutlined style={{ fontSize: 32, color: 'var(--color-primary, #024DC4)' }} />
        </div>
        <h2 className="da-title">OPC UA 连接管理</h2>
        <p className="da-subtitle">选择常用连接或创建新连接</p>
      </div>

      <div className="da-list-content">
        <Spin spinning={loading}>
          {connections.length === 0 ? (
            <Empty
              image={<DesktopOutlined style={{ fontSize: 64, color: '#C9CDD4' }} />}
              description={
                <span>
                  暂无常用连接
                  <br />
                  点击下方按钮创建新连接
                </span>
              }
            />
          ) : (
            <div className="da-connections-list">
              {connections.map((conn) => (
                <div
                  key={conn.id}
                  className="da-connection-item"
                  onClick={() => handleConnect(conn)}
                >
                  <div className="da-connection-info">
                    <div className="da-conn-icon">
                      {/* <ServerOutlined
                        style={{ fontSize: 20, color: 'var(--color-primary, #024DC4)' }}
                      /> */}
                      <DatabaseTwoTone style={{ fontSize: 24, color: 'var(--color-primary, #024DC4)' }} />
                    </div>
                    <div className="da-connection-details">
                      <div className="da-connection-name">{conn.name}</div>
                      <div className="da-conn-url">{conn.server_url}</div>
                      <div className="da-conn-user">用户: {conn.username}</div>
                      {conn.auto_connect === 1 && (
                        <Tag color="blue" style={{ marginTop: 4 }}>
                          自动连接
                        </Tag>
                      )}
                    </div>
                  </div>
                  <div className="da-connection-actions">
                    <button
                      className="da-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(conn);
                      }}
                    >
                      <EditOutlined style={{ color: '#718096',fontSize: 18 }} />
                    </button>
                    <span onClick={(e) => e.stopPropagation()}>
                      <Popconfirm
                        title="确认删除"
                        description={`确定要删除连接 "${conn.name}" 吗？删除后将清空缓存数据。`}
                        onConfirm={() => handleDelete(conn.id)}
                        okText="删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                      >
                        <button className="da-action-btn">
                          <DeleteOutlined style={{ color: '#F53F3F',fontSize: 18 }} />
                        </button>
                      </Popconfirm>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Spin>
      </div>

      <div className="da-list-footer">
        <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleNew}>
          新建连接
        </Button>
      </div>
    </div>
  );

  // ============================================================
  // 渲染: 表单视图
  // ============================================================
  const renderFormView = () => (
    <div className="da-form-container">
      <div className="da-form-topbar">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToList}
          style={{ padding: 0 }}
        >
          返回列表
        </Button>
      </div>

      <div className="da-form-wrapper">
        <Card className="da-form-card" bordered={false}>
          <div className="da-form-header">
            <div className="da-icon-box">
              <ThunderboltOutlined
                style={{ fontSize: 32, color: 'var(--color-primary, #024DC4)' }}
              />
            </div>
            <h2 className="da-title">{editingId ? '编辑连接' : '新建连接'}</h2>
            <p className="da-subtitle">配置 OPC UA 服务器连接参数</p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            autoComplete="off"
            className="da-form"
          >
            <Form.Item
              label="连接名称"
              name="name"
              rules={[{ required: true, message: '请输入连接名称' }]}
            >
              <Input placeholder="例如：生产服务器" maxLength={50} />
            </Form.Item>

            <Form.Item
              label={
                <div className="da-label-row">
                  <span>服务器地址</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<ExperimentOutlined />}
                    onClick={handleMockFill}
                    style={{ padding: '0 4px', fontSize: 12 }}
                  >
                    模拟数据
                  </Button>
                </div>
              }
              name="server_url"
              rules={[{ required: true, message: '请输入服务器地址' }]}
            >
              <Input placeholder="opc.tcp://192.168.2.102:4840 或 mock://demo" />
            </Form.Item>

            <div className="da-form-row">
              <Form.Item
                label="用户名"
                name="username"
                style={{ flex: 1 }}
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input placeholder="admin" />
              </Form.Item>

              <Form.Item label="密码" name="password" style={{ flex: 1 }}>
                <Input.Password placeholder="请输入密码" />
              </Form.Item>
            </div>

            <Form.Item label="自动连接" name="auto_connect" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="自动重连" name="auto_reconnect" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block size="large">
                {editingId ? '保存修改' : '保存并连接'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );

  // ============================================================
  // 渲染: 连接中视图
  // ============================================================
  const renderConnectingView = () => {
    const info = connectingInfo;
    if (!info) return null;

    const isError = info.status === 'error';
    const isSuccess = info.status === 'success';

    return (
      <div className="da-connecting-container">
        <div className="da-connecting-content">
          {/* 动画区域 */}
          <div className="da-spinner-area">
            <div className={`da-spinner-outer ${isError ? 'da-error' : ''} ${isSuccess ? 'da-success' : ''}`}>
              <div className="da-spinner-ring" />
              <div className="da-spinner-inner">
                {isError ? (
                  <CloseCircleOutlined style={{ fontSize: 40, color: '#F53F3F' }} />
                ) : isSuccess ? (
                  <CheckCircleOutlined style={{ fontSize: 40, color: '#00B42A' }} />
                ) : (
                  <SyncOutlined spin style={{ fontSize: 40, color: '#fff' }} />
                )}
              </div>
            </div>
          </div>

          {/* 进度条 */}
          <div className="da-progress-area">
            <Progress
              percent={Math.round(connectProgress)}
              status={isError ? 'exception' : isSuccess ? 'success' : 'active'}
              strokeColor={
                isError
                  ? '#F53F3F'
                  : isSuccess
                    ? '#00B42A'
                    : 'var(--color-primary, #024DC4)'
              }
            />
          </div>

          {/* 状态信息 */}
          <div className="da-status-info">
            {isSuccess && (
              <div className="da-points-count">
                <ThunderboltOutlined
                  style={{ fontSize: 24, color: 'var(--color-primary, #024DC4)' }}
                />
                <div>
                  <div className="da-points-label">已发现点位</div>
                  <div className="da-points-value">
                    {info.connectedNodes}
                    <span className="da-points-total">/{info.totalNodes}</span>
                  </div>
                </div>
              </div>
            )}
            <p className={`da-status-message ${isError ? 'da-error-text' : ''} ${isSuccess ? 'da-success-text' : ''}`}>
              {info.message}
            </p>
          </div>

          {/* 操作按钮 */}
          {!isSuccess && !isError && (
            <Button
              danger
              size="large"
              icon={<StopOutlined />}
              onClick={handleCancelConnect}
              className="da-cancel-btn"
            >
              终止连接
            </Button>
          )}
          {isSuccess && (
            <Button type="primary" size="large" onClick={handleBackToList}>
              返回列表
            </Button>
          )}
          {isError && (
            <Space>
              <Button size="large" onClick={handleCancelConnect}>
                返回列表
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={() => handleConnect(info.connection)}
              >
                重试连接
              </Button>
            </Space>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // 主渲染
  // ============================================================
  const renderContent = () => {
    switch (viewMode) {
      case 'form':
        return renderFormView();
      case 'connecting':
        return renderConnectingView();
      default:
        return renderListView();
    }
  };

  return (
    <>
      <TopBar title="数据接入" mainContent={
                    <Navbar />
                  } extra={extra} />
      
      <div className="da-page">{renderContent()}</div>
    </>
  );
};

export default DataAccess;
